import { useQueryClient } from '@tanstack/react-query';
import cx from 'clsx';
import React, { useState } from 'react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ReactComponent as DeleteImageIcon } from '@assets/svg/ic-delete-image.svg';

import { useEquipmentAddPhotoMutation } from '@components/common/Gallery/services/useEquipmentAddPhotoMutation';
import { useEquipmentDeletePhotoMutation } from '@components/common/Gallery/services/useEquipmentDeletePhotoMutation';
import { useEquipmentSensorDeletePhotoMutation } from '@components/common/Gallery/services/useEquipmentSensorDeletePhotoMutation';
import PhotoLabel from '@components/common/PhotoLabel/PhotoLabel';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { getUrlResizeImage } from '@helpers/getUrlResizeImage';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { hardwareOverviewEquipmentItemKey } from '@pages/EquipmentDetails/services/useHardwareOverviewEquipmentItemQuery';

import { useEquipmentImageAttachMutation } from '@services/equipment/useEquipmentImageAttachMutation';

import { selectAppRtl } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import Dropzone from '../AttachFilesModal/components/Dropzone/Dropzone';
import ModalHeader from '../ModalHeader/ModalHeader';
import style from './style.module.scss';

const acceptFiled = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'application/pdf': ['.pdf'],
};

const EditPhotosModal = ({ photos, equipmentType, equipmentId, itemId }) => {
  const { t } = useTranslation();
  const rtl = selectAppRtl();
  const queryClient = useQueryClient();

  const { setSecondaryModal, setModalClose } = useModalActions();

  const [localFiles, setLocalFiles] = useState([]);
  const [indexPhoto, setIndexPhoto] = useState(0);
  const [deletedIds, setDeletedIds] = useState({});

  const successAction = () => {
    showToast({ variant: 'success', message: t('The updates have been saved.') });

    queryClient.invalidateQueries({ queryKey: [hardwareOverviewEquipmentItemKey()[0]] });
    setModalClose();
    setDeletedIds({});
  };

  const errorAction = () => {
    showToast({ variant: 'error', message: t('Something went wrong!') });
  };

  const { mutate: equipmentAddPhotoMutate, isLoading: equipmentAddPhotoIsLoading } =
    useEquipmentAddPhotoMutation();
  const { mutate: equipmentDeletePhotoMutate, isLoading: equipmentDeletePhotoIsLoading } =
    useEquipmentDeletePhotoMutation({ onError: errorAction });
  const { mutate: equipmentImageAttachMutate, isLoading: equipmentImageAttachIsLoading } =
    useEquipmentImageAttachMutation();
  const { mutate: equipmentSensorDeletePhotoMutate, isLoading: equipmentSensorDeletePhotoIsLoading } =
    useEquipmentSensorDeletePhotoMutation({ onError: errorAction });

  const isLoadingMutation =
    equipmentAddPhotoIsLoading ||
    equipmentDeletePhotoIsLoading ||
    equipmentImageAttachIsLoading ||
    equipmentSensorDeletePhotoIsLoading;

  const deleteImages = () => {
    const deletedIdsKeys = Object.keys(deletedIds);
    if (!deletedIdsKeys.length) return;

    deletedIdsKeys.forEach((photoId) => {
      if (!deletedIds[photoId]?.isDeleted) return;

      if (equipmentType === 'mote' || equipmentType === 'sensor' || equipmentType === 'tachometer') {
        equipmentSensorDeletePhotoMutate(
          { sourceFrom: deletedIds[photoId].sourceFrom, imageId: photoId },
          {
            onSuccess: () => {
              if (!localFiles.length) {
                successAction();
              }
            },
          },
        );
      } else {
        equipmentDeletePhotoMutate(
          {
            equipmentType: equipmentType === 'repeater' ? 'router' : equipmentType,
            photoId,
          },
          {
            onSuccess: () => {
              if (!localFiles.length) {
                successAction();
              }
            },
          },
        );
      }
    });
  };

  const deleteLocalImage = (index) => {
    const newPhotosArr = localFiles.filter((file, idx) => idx !== index);

    setIndexPhoto(0);
    setLocalFiles(newPhotosArr);
  };

  const saveChanges = () => {
    if (!Object.values(deletedIds).filter(Boolean).length && !localFiles.length) {
      setModalClose();
      return;
    }
    deleteImages();

    if (!localFiles.length) {
      return;
    }

    Promise.all(
      localFiles.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
          }),
      ),
    ).then((images) => {
      if (equipmentType === 'mote' || equipmentType === 'sensor' || equipmentType === 'tachometer') {
        equipmentImageAttachMutate(
          {
            images,
            equipment_id: equipmentId,
            installation_point_id: itemId,
          },
          {
            onSuccess: () => {
              successAction();
            },
            onError: () => errorAction(),
          },
        );
      } else {
        equipmentAddPhotoMutate(
          {
            equipmentType: equipmentType === 'repeater' ? 'router' : equipmentType,
            itemId,
            newPhotos: images,
          },
          {
            onSuccess: () => {
              successAction();
            },
            onError: () => errorAction(),
          },
        );
      }
    });
  };

  const allPhotos = [...photos, ...localFiles];

  const handleConfirm = (photo, sourceFrom) => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        onConfirm: () => setDeletedIds({ ...deletedIds, [photo.id]: { isDeleted: true, sourceFrom } }),
        bodyTitle:
          photo.sourceFrom === 'mainImage'
            ? t('Are you sure you want to delete this photo?')
            : t('Are you sure you want to delete the photo?'),
        bodyContent:
          photo.sourceFrom === 'mainImage'
            ? t(
                'It is currently set as the Main Asset photo and will also be removed from all associated installation points.',
              )
            : undefined,
      },
    });
  };

  return (
    <div className={cx(style.editPhotosModal, { [style.bodyRTL]: rtl })}>
      <ModalHeader title={t('Edit Photos')} />
      <div className={style.modalBody}>
        <div className={style.photoBlock}>
          {!allPhotos.length ? (
            <div className={style.emptyBlockPhotos}>{t('No photos')}</div>
          ) : (
            <>
              <img
                className={style.mainPhoto}
                src={allPhotos[indexPhoto]?.url || allPhotos[indexPhoto]?.preview}
                alt={t('photo')}
              />
              <PhotoLabel sourceFrom={allPhotos[indexPhoto].sourceFrom} />
            </>
          )}
        </div>

        <div className={style.photosListBlock}>
          <Dropzone
            maxFilesCount={10}
            className={style.dropzone}
            typeOfFiles={acceptFiled}
            variant="photoBtn"
            currentFiles={localFiles}
            onDrop={setLocalFiles}
          />
          <Swiper
            modules={[Pagination, Navigation]}
            spaceBetween={8}
            slidesPerView={7}
            className={style.gallerySwiper}
            pagination={{ clickable: true, dynamicBullets: true, dynamicMainBullets: 4 }}
          >
            {!!photos.length &&
              photos.map((photo, i) => (
                <SwiperSlide
                  key={i}
                  className={cx(style.photoItemWrapper, {
                    [style.deletedPhoto]:
                      deletedIds[photo.id]?.isDeleted &&
                      deletedIds[photo.id]?.sourceFrom === photo.sourceFrom,
                  })}
                >
                  <img
                    className={style.photoItem}
                    src={getUrlResizeImage(photo.url, '150_200')}
                    alt={t(`photo-${i}`)}
                    onClick={() => setIndexPhoto(i)}
                  />
                  {deletedIds[photo.id]?.isDeleted &&
                  deletedIds[photo.id]?.sourceFrom === photo.sourceFrom ? (
                    <div
                      className={style.recoverImageBtn}
                      onClick={() =>
                        setDeletedIds({ ...deletedIds, [photo.id]: { isDeleted: false, sourceFrom: '' } })
                      }
                    >
                      {t('Recover')}
                    </div>
                  ) : (
                    <DeleteImageIcon
                      className={style.deleteImageIcon}
                      onClick={() => handleConfirm(photo, photo.sourceFrom)}
                    />
                  )}
                </SwiperSlide>
              ))}
            {!!localFiles.length &&
              localFiles.map((photo, i) => (
                <SwiperSlide key={i} className={cx(style.photoItemWrapper)}>
                  <img
                    className={style.photoItem}
                    src={photo.preview}
                    alt={t(`photo-${i}`)}
                    onClick={() => setIndexPhoto(photos.length + i)}
                  />
                  <DeleteImageIcon className={style.deleteImageIcon} onClick={() => deleteLocalImage(i)} />
                </SwiperSlide>
              ))}
          </Swiper>
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} disabled={isLoadingMutation} onClick={setModalClose} />
        <Button
          type="primary"
          variant={'medium'}
          text={t('Save')}
          disabled={isLoadingMutation}
          onClick={saveChanges}
        />
      </div>
    </div>
  );
};

export default EditPhotosModal;
